import { ListRenderItem } from '@shopify/flash-list';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { sortBy, without } from 'lodash';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  PAUSE_BG_NETWORK,
  doCloudPropertiesFilter,
  UNPAUSE_BG_NETWORK,
  doCloudPropertiesClosed,
  doCloudPropertiesResetModal,
  eventSelectScreenSelection,
  doSyncDownloadProperties,
} from 'store/actions';
import {
  selectSearchAssetIds,
  selectSearchLoadKey,
  selectSearchTotal,
} from 'store/search/selectors';

import { doAddEvent, doRemoveEvents } from 'lib/events';
import { randomKey } from 'lib/riskbase';
import { CloudAssetsHeader } from './CloudAssetsHeader';
import { ListItem } from './ListItem';
import { View } from 'react-native';
import { theme } from 'lib/theme';
import { Txt } from 'components/Txt';
import { List } from 'components/List';
import { PageEmpty } from 'components/PageEmpty';

const renderItemWithCacheKey =
  (cacheKey: string): ListRenderItem<string> =>
  ({ item, extraData }) => (
    <ListItem
      id={item}
      cacheKey={cacheKey}
      onSelect={extraData.onSelect}
      selected={extraData.selectedPropertyIds.includes(item)}
    />
  );

export const CloudAssetsScreen: React.FC<
  ModalScreenProps<ModalScreens.CLOUDASSETS>
> = ({ navigation, route }) => {
  const { organisations, organisationIds: initOrganisationIds } = route.params;
  const dispatch = useDispatch();

  const hasSomePropertiesLoaded = React.useRef(false);
  const eventKeyOrganisationSelect = React.useRef(randomKey());
  const searchLoadKey = useSelector(selectSearchLoadKey);
  const propertyIds = useSelector(selectSearchAssetIds);
  hasSomePropertiesLoaded.current =
    hasSomePropertiesLoaded.current || !!propertyIds.length;

  const [filter, setFilter] = React.useState<{
    search: string;
    organisationIds: string[];
    sortId: 'relevant:asc' | 'name:asc' | 'name:desc';
  }>({
    search: '',
    organisationIds: initOrganisationIds,
    sortId: 'relevant:asc',
  });

  const [selectedPropertyIds, setSelectedPropertyIds] = React.useState<
    string[]
  >([]);
  const hasPropertiesSelected = !!selectedPropertyIds.length;

  const cacheKey = React.useRef(Date.now().toString());
  const renderItem = React.useMemo(
    () => renderItemWithCacheKey(cacheKey.current),
    [],
  );

  const handleShowOrganisationPicker = React.useCallback(
    () =>
      dispatch(
        NAVIGATE_MODAL({
          screen: ModalScreens.SELECT,
          params: {
            title: 'Download Properties',
            subtitle: 'Select Organisations',
            optionNoun: 'Organisation',
            multiSelect: true,
            searchable: true,
            eventKey: eventKeyOrganisationSelect.current,
            showQuickChoose: true,
            value: filter.organisationIds,
            options: sortBy(Object.values(organisations), ['name']).map(
              (org) => ({
                name: org.name,
                internalValue: org.id,
              }),
            ),
          },
        }),
      ),
    [dispatch, filter.organisationIds, organisations],
  );

  const handleClose = React.useCallback(() => {
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch]);

  const onSelect = React.useCallback((propertyId: string) => {
    setSelectedPropertyIds((existing) =>
      existing.includes(propertyId)
        ? without(existing, propertyId)
        : [...existing, propertyId],
    );
  }, []);

  React.useEffect(() => {
    dispatch(
      doCloudPropertiesFilter({
        organisationIds: filter.organisationIds,
        search: filter.search,
        sortId: filter.sortId,
      }),
    );
  }, [
    dispatch,
    filter.organisationIds,
    filter.search,
    filter.sortId,
    searchLoadKey,
  ]);

  React.useEffect(() => {
    dispatch(PAUSE_BG_NETWORK());
    return () => {
      dispatch(doCloudPropertiesClosed());
      dispatch(UNPAUSE_BG_NETWORK());
    };
  }, [dispatch]);

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !hasPropertiesSelected });
  }, [navigation, hasPropertiesSelected]);
  useEsc(handleClose);

  React.useEffect(() => {
    const events = [
      doAddEvent(eventSelectScreenSelection, (event) => {
        if (event.payload.key === eventKeyOrganisationSelect.current) {
          setFilter((c) => ({
            ...c,
            organisationIds: event.payload.stringValues,
          }));
        }
      }),
      doAddEvent(doCloudPropertiesResetModal, () => setSelectedPropertyIds([])),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [dispatch]);

  const total = useSelector(selectSearchTotal);
  const totalToLoad = total ? total - propertyIds.length : 0;
  const showLoadMore = !!totalToLoad;

  return (
    <Page
      header={
        <CloudAssetsHeader
          onClose={handleClose}
          openOrganisationFilter={handleShowOrganisationPicker}
          onDownloadPress={() =>
            dispatch(doSyncDownloadProperties(selectedPropertyIds))
          }
          onChangeSort={(value) => setFilter((c) => ({ ...c, sortId: value }))}
          onSearch={(value) => setFilter((c) => ({ ...c, search: value }))}
          organisationOptions={organisations}
          sortOptions={[
            {
              id: 'relevant:asc',
              name: 'Relevant',
            },
            {
              id: 'name:asc',
              name: 'Name A-Z',
            },
            {
              id: 'name:desc',
              name: 'Name Z-A',
            },
          ]}
          organisationIds={filter.organisationIds}
          searchText={filter.search}
          sortId={filter.sortId}
          selectedPropertyIds={selectedPropertyIds}
        />
      }
    >
      {!propertyIds.length ? (
        <PageEmpty
          icon={ICONS.RBPlectrumSolidCutout}
          text="No Properties Found"
          subtext="Try changing your filters"
        />
      ) : (
        <List
          data={propertyIds}
          extraData={{
            onSelect,
            selectedPropertyIds,
          }}
          keyExtractor={(item) => item}
          ListFooterComponent={
            <View
              style={{
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 85,
                paddingTop: theme.grid,
              }}
            >
              {showLoadMore && (
                <View style={{ alignItems: 'center' }}>
                  <Txt style={{ textAlign: 'center' }}>
                    {`${Number(
                      totalToLoad,
                    ).toLocaleString()} More Asset${totalToLoad === 1 ? '' : 's'}`}
                  </Txt>
                  <Txt style={{ textAlign: 'center' }} weight="bold">
                    Try changing your filters
                  </Txt>
                </View>
              )}
              {total !== null && !totalToLoad && (
                <View style={{ alignItems: 'center' }}>
                  <Txt style={{ textAlign: 'center' }}>
                    {total === 0 ? 'No Assets Available' : 'No More Assets'}
                  </Txt>
                  {(!!filter.search || !!filter.organisationIds.length) && (
                    <Txt style={{ textAlign: 'center' }} weight="bold">
                      Try changing or removing filters
                    </Txt>
                  )}
                </View>
              )}
            </View>
          }
          renderItem={renderItem}
          ItemSeparatorComponent={() => (
            <View style={{ height: theme.padding / 2 }} />
          )}
        ></List>
      )}
    </Page>
  );
};
